import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ChevronRight,
  PoundSterling,
  TrendingUp,
  Calendar,
  CheckCircle2,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  FileText,
  AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { CompanyDetailsSidebar, mockCompanyData } from "@/components/analytics/company-details-sidebar";
import { CUSTOMERS } from "@/mockData/customers";
import { CUSTOMER_ACTIVITIES, COLLECTION_CASES } from "@/mockData/customerActivity";

// Mock 30-day trend data
const mock30DayTrend = [
  { day: 'Day 1', dso: 48, cei: 85 },
  { day: 'Day 5', dso: 50, cei: 83 },
  { day: 'Day 10', dso: 52, cei: 81 },
  { day: 'Day 15', dso: 49, cei: 84 },
  { day: 'Day 20', dso: 47, cei: 86 },
  { day: 'Day 25', dso: 45, cei: 88 },
  { day: 'Day 30', dso: 45, cei: 87 },
];

// Activity icon/colour helpers
const activityConfig: Record<string, { icon: React.ElementType; color: string }> = {
  payment:  { icon: CheckCircle2, color: 'text-green-600' },
  promise:  { icon: Clock,        color: 'text-blue-600'  },
  email:    { icon: Mail,         color: 'text-purple-600'},
  phone:    { icon: Phone,        color: 'text-teal-600'  },
  dispute:  { icon: MessageSquare,color: 'text-orange-600'},
  case:     { icon: AlertCircle,  color: 'text-red-600'   },
  note:     { icon: FileText,     color: 'text-gray-600'  },
  system:   { icon: Clock,        color: 'text-gray-500'  },
};

function getActivityConfig(type: string) {
  return activityConfig[type] ?? { icon: Clock, color: 'text-gray-500' };
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

export default function CompanyDetailsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const company = mockCompanyData.find((c) => c.id === companyId);

  // Top customers: shared mock data sorted by totalOutstanding desc
  const topCustomers = useMemo(
    () => [...CUSTOMERS].sort((a, b) => b.totalOutstanding - a.totalOutstanding).slice(0, 10),
    []
  );

  // Today's priorities: customers in active collection, sorted by over90 desc
  const todaysPriorities = useMemo(
    () =>
      COLLECTION_CASES.slice(0, 8).map((c) => {
        const customer = CUSTOMERS.find((cu) => cu.id === c.customerId);
        return { ...c, customerName: customer?.name ?? c.customerId };
      }),
    []
  );

  // Recent activity: latest activities across all customers
  const recentActivity = useMemo(
    () =>
      [...CUSTOMER_ACTIVITIES]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10),
    []
  );

  const formatCurrency = (value: number, currency: string) => {
    const locale = currency === 'USD' ? 'en-US' : 'en-GB';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };


  if (!company) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground text-lg">Company not found.</p>
          <Button onClick={() => navigate('/analytics-reporting/multi-company')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Sidebar */}
      <div
        className={cn(
          "relative transition-all duration-300 ease-in-out shrink-0",
          isSidebarCollapsed ? "w-0" : "w-80"
        )}
      >
        <CompanyDetailsSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(true)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Collapsed sidebar toggle handle */}
        {isSidebarCollapsed && (
          <button
            onClick={() => setIsSidebarCollapsed(false)}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-40 group"
            title="Show Companies"
          >
            <div className="flex items-center bg-teal-500 hover:bg-teal-600 text-white shadow-lg rounded-r-lg transition-all duration-200 group-hover:pr-2">
              <div className="flex flex-col items-center justify-center py-4 px-2 gap-1">
                <ChevronRight className="h-5 w-5" />
                <div className="[writing-mode:vertical-lr] text-xs font-medium tracking-wider rotate-180">
                  COMPANIES
                </div>
              </div>
            </div>
          </button>
        )}

        <div className="flex flex-col h-full overflow-hidden">
          {/* Page Header */}
          <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex-shrink-0">
            <div className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/analytics-reporting/multi-company')}
                      className="gap-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Button>
                    <div className="h-6 w-px bg-border" />
                    <h1 className="text-2xl font-bold tracking-tight">{company.name}</h1>
                    <Badge variant="outline" className="font-mono text-sm">
                      {company.currency}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm ml-[88px]">
                    Company performance overview and analytics
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">

              {/* Key Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StaticMetricCardAlt
                  title="TOTAL OUTSTANDING"
                  value={formatCurrency(company.totalAR, company.currency)}
                  change={company.arTrend}
                  changeLabel="vs last month"
                  icon={PoundSterling}
                  variant="info"
                />
                <StaticMetricCardAlt
                  title="TOTAL OVERDUE"
                  value={formatCurrency(company.overdue, company.currency)}
                  change={`${company.overduePercent.toFixed(1)}%`}
                  changeLabel="of total AR"
                  icon={TrendingUp}
                  variant="error"
                />
                <StaticMetricCardAlt
                  title="DSO"
                  value={`${company.dso} days`}
                  change={company.dsoTrend}
                  changeLabel="vs last month"
                  icon={Calendar}
                  variant="warning"
                />
                <StaticMetricCardAlt
                  title="CEI"
                  value={`${company.cei}%`}
                  change={company.ceiTrend}
                  changeLabel="vs last month"
                  icon={CheckCircle2}
                  variant="success"
                />
              </div>

              {/* Additional Stats Row */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Avg Days to Pay</p>
                  <p className="text-2xl font-bold mt-1">{company.avgDaysToPay} days</p>
                  <p className="text-xs text-muted-foreground mt-1">Average payment speed</p>
                </Card>
                <Card className="p-4">
                  <p className="text-sm text-muted-foreground">Payment Speed</p>
                  <div className="mt-1">
                    <Badge
                      variant={
                        (() => {
                          if (company.paymentSpeed === 'Improving') return 'default';
                          if (company.paymentSpeed === 'Declining') return 'destructive';
                          return 'secondary';
                        })()
                      }
                      className="text-sm px-3 py-1"
                    >
                      {company.paymentSpeed}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Trend over last 30 days</p>
                </Card>
              </div>

              {/* 30-Day Performance Trend */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">30-Day Performance Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={mock30DayTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="dso"
                      stroke="#00897B"
                      strokeWidth={2}
                      name="DSO"
                      dot={{ fill: '#00897B', r: 4 }}
                      animationDuration={1500}
                    />
                    <Line
                      type="monotone"
                      dataKey="cei"
                      stroke="#2196F3"
                      strokeWidth={2}
                      name="CEI"
                      dot={{ fill: '#2196F3', r: 4 }}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>

              {/* Today's Priorities & Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Today's Priorities */}
                <Card className="p-6 flex flex-col">
                  <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Today's Priorities</h3>
                  <div className="overflow-y-auto max-h-64 space-y-3 pr-1">
                    {todaysPriorities.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => navigate('/customers')}
                        className="w-full text-left p-4 border-l-4 border-l-teal-500 bg-muted/30 rounded-r-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-gray-900 truncate">{item.customerName}</p>
                            <p className="text-sm text-muted-foreground mt-0.5 truncate">{item.nextAction}</p>
                          </div>
                          <Badge
                            variant={item.priority === 'high' ? 'destructive' : 'secondary'}
                            className="shrink-0 capitalize text-xs"
                          >
                            {item.priority}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Due: {item.dueDate}</p>
                      </button>
                    ))}
                  </div>
                </Card>

                {/* Recent Activity */}
                <Card className="p-6 flex flex-col">
                  <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Recent Activity</h3>
                  <div className="overflow-y-auto max-h-64 space-y-4 pr-1">
                    {recentActivity.map((activity) => {
                      const { icon: Icon, color } = getActivityConfig(activity.type);
                      const customer = CUSTOMERS.find((c) => c.id === activity.customerId);
                      return (
                        <button
                          key={activity.id}
                          type="button"
                          onClick={() => navigate('/customers')}
                          className="w-full text-left flex items-start gap-3 hover:bg-muted/40 rounded-lg p-1 -mx-1 transition-colors"
                        >
                          <div className={cn("p-2 rounded-full bg-muted shrink-0", color)}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{activity.title}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {customer?.name ?? activity.customerId}
                            </p>
                          </div>
                          <p className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {timeAgo(activity.date)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </Card>
              </div>

              {/* Top Customers by Outstanding */}
              <Card className="p-6 flex flex-col">
                <h3 className="text-lg font-semibold mb-4 flex-shrink-0">Top Customers by Outstanding</h3>
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-y-auto max-h-64">
                    <table className="w-full">
                      <thead className="bg-muted sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium">Customer</th>
                          <th className="text-right p-3 text-sm font-medium">Outstanding</th>
                          <th className="text-right p-3 text-sm font-medium">Over 90 Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCustomers.map((customer) => (
                          <tr
                            key={customer.id}
                            className="border-t hover:bg-muted/50 transition-colors"
                          >
                            <td className="p-3 text-sm font-medium">
                              <button
                                onClick={() => navigate(`/customers?search=${encodeURIComponent(customer.name)}`)}
                                className="text-primary hover:underline text-left"
                              >
                                {customer.name}
                              </button>
                            </td>
                            <td className="text-right p-3 text-sm font-medium">
                              {formatCurrency(customer.totalOutstanding, company.currency)}
                            </td>
                            <td className="text-right p-3 text-sm text-red-600 font-medium">
                              {customer.over90 > 0
                                ? formatCurrency(customer.over90, company.currency)
                                : <span className="text-muted-foreground">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
